/**
 * Split the facility's point clouds into PROJECT scans and INDIVIDUAL scans for
 * the surveyor-facing scan selector.
 *
 * WHY THE CLASSIFIER LOOKS LIKE THIS — measured against the live EDF facility
 * (142 point clouds, 8 projects):
 *
 *   pointCloud.projectId  ->  null on 136 of 142. The 6 that carry one all point
 *                             at panorama projects named "2026", not at the scan
 *                             projects. Unusable as a grouping key.
 *   state.levels          ->  0 entries.
 *   state.forgeImages     ->  0 entries, so the documented
 *                             "individual scan == matches a panorama name" test
 *                             classifies NOTHING here.
 *
 * The only signal that survives contact with the data is the name:
 *
 *   `1HDC0607ZL`      matches a project name        -> PROJECT scan (combined)
 *   `1HDC0607ZL-001`  a capture setup within it     -> INDIVIDUAL scan
 *
 * That distinction is what the surveyor needs: a project scan gives 360°
 * multi-side coverage so deviation is visible from any angle, while an
 * individual scan is a single viewpoint and can leave the verified feature in an
 * occlusion shadow. Picking the wrong kind produces a confident, wrong reading.
 *
 * DIFFERS FROM `pickScanForTagPoint` ON PURPOSE: that function must never
 * AUTO-select a superseded `(old)` revision, so it drops them. This one KEEPS
 * them and flags `superseded: true`, because deliberately comparing against a
 * previous revision is a legitimate thing for a surveyor to do — it just must
 * never happen by accident. Both use the same `SUPERSEDED_NAME` definition.
 *
 * Pure function.
 *
 * @param {Array<{name: string, url: string, createDate: string}>} pointClouds
 * @param {Array<{name: string, isArchived?: boolean}>} projects
 * @returns {{ projectScans: Array<object>, individualScans: Array<object> }}
 *   Both lists sorted by name. Individuals carry `parent` (the project scan they
 *   belong to by name prefix, or null) so the UI can show which building a
 *   capture setup came from.
 */
import { SUPERSEDED_NAME, uploadedAt } from '@/features/Verification/utils/pickScanForTagPoint';

export function classifyScans(pointClouds, projects) {
  const clouds = Array.isArray(pointClouds) ? pointClouds : [];
  if (clouds.length === 0) return { projectScans: [], individualScans: [] };

  // Names that identify a whole-project (combined) capture. Archived projects
  // still count — their scan may be exactly the historical revision a surveyor
  // is reaching for; it simply won't be picked automatically.
  const projectNames = new Set(
    (Array.isArray(projects) ? projects : []).map((p) => p?.name).filter(Boolean),
  );

  // One entry per name, newest upload wins. `1HDC0607ZL` exists 4x live, so
  // without this the list shows the same scan repeatedly with no way to tell
  // the entries apart.
  const newestByName = new Map();
  for (const cloud of clouds) {
    if (!cloud?.name) continue;
    const existing = newestByName.get(cloud.name);
    if (!existing || uploadedAt(cloud) > uploadedAt(existing)) {
      newestByName.set(cloud.name, cloud);
    }
  }

  const projectScans = [];
  const individualScans = [];

  for (const cloud of newestByName.values()) {
    const entry = {
      name: cloud.name,
      url: cloud.url,
      createDate: cloud.createDate || null,
      superseded: SUPERSEDED_NAME.test(cloud.name),
      raw: cloud,
    };
    if (projectNames.has(cloud.name)) projectScans.push(entry);
    else individualScans.push(entry);
  }

  const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true });
  projectScans.sort(byName);

  // Attribute each capture setup to the project scan whose name it starts with,
  // so the UI can group `…-001, -002, …` under the building they came from.
  // Longest match wins: with both `1HDC06` and `1HDC0607ZL` present, the more
  // specific one is the real parent.
  const parentCandidates = projectScans
    .map((p) => p.name)
    .sort((a, b) => b.length - a.length);
  for (const entry of individualScans) {
    entry.parent = parentCandidates.find((name) => entry.name.startsWith(name)) || null;
  }
  individualScans.sort(byName);

  return { projectScans, individualScans };
}

export default classifyScans;
